import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Container, Col, Row, Spinner } from "react-bootstrap";
import moment from "moment-timezone";
import InfiniteScroll from "react-infinite-scroll-component";
import { healerServices } from "@/services/healerServices";
import Layout from "@/components/layout";
import dummyImg from "../../../public/images/blog-1.jpg";
import styles from "@/styles/Blogs.module.css";
import Head from "next/head";

const Blogs = (props) => {
  const [sliderData, setSliderData] = useState(props?.sliderData ?? []);
  const [blogsMeta, setBlogsMeta] = useState(props?.blogsMeta ?? {});
  const [hasMore, setHasMore] = useState(true);
  const [listIndex, setListIndex] = useState(1);
  const [showSideButton, setShowSideButton] = useState(false);
  const firstSectionRef = useRef(null);

  const pageRef = useRef(1);
  const isFetching = useRef(false);
  const loaderRef = useRef(null); // ⬅️ NEW (only minimal change)

  useEffect(() => {
    const handleScroll = () => {
      if (!firstSectionRef.current) return;
      const sectionTop = firstSectionRef.current.getBoundingClientRect().bottom;
      setShowSideButton(sectionTop < 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 🔥 Loader detect → Force API call
  useEffect(() => {
    const onScroll = () => {
      if (!loaderRef.current || !hasMore) return;

      const { top } = loaderRef.current.getBoundingClientRect();

      if (top < window.innerHeight && !isFetching.current) {
        fetchSliderData(true);
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll(); // run once on mount (important!)

    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore]);

  const fetchSliderData = async (scroll = null) => {
    try {
      if (isFetching.current) return;
      isFetching.current = true;

      let pageToFetch;

      if (scroll) {
        pageRef.current += 1;
        pageToFetch = pageRef.current;
      } else {
        pageRef.current = 1;
        pageToFetch = 1;
        setSliderData([]);
        setHasMore(true);
      }

      setListIndex(pageRef.current);

      const payload = { page: pageToFetch };
      const res = await healerServices.getBlogsData(payload);

      if (res?.status) {
        const incoming = res.data?.data ?? [];

        if (incoming.length > 0) {
          setSliderData((prev) => [...prev, ...incoming]);
          setHasMore(true);
        } else {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setHasMore(false);
    } finally {
      isFetching.current = false;
    }
  };

  const validateImgUrl = (str) => {
    var pattern = new RegExp(
      "^(https?:\\/\\/)?" +
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
        "((\\d{1,3}\\.){3}\\d{1,3}))" +
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" +
        "(\\?[;&a-z\\d%_.~+=-]*)?" +
        "(\\#[-a-z\\d_]*)?$",
      "i"
    );
    return !!pattern.test(str);
  };

  return (
    <>
      <Head>
        <title>{blogsMeta?.meta?.og_title}</title>
        <meta name="title" content={blogsMeta?.meta?.meta_title}></meta>
        <meta
          name="description"
          content={blogsMeta?.meta?.meta_description}
        ></meta>
        <meta name="keywords" content={blogsMeta?.meta?.meta_keywords}></meta>
      </Head>

      <section
        className={styles["section-banner"]}
        style={{ background: "url(/images/healerlisting-banner.jpg)" }}
        ref={firstSectionRef}
      >
        <Container>
          <Row>
            <Col md={10} className="m-auto">
              <div className={styles["inner-content"]}>
                <h1 className={styles["banner-title"]}>
                  Our Latest & Most Popular Tips & Tricks For You
                </h1>
                <h2>
                  Empower Yourself with Healing Energy. Learn Reiki Techniques
                  for Self-care and Well-being.
                </h2>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className={styles["blog-section"]}>
        <Container>
          <InfiniteScroll
            dataLength={sliderData.length}
            next={() => fetchSliderData(true)}
            hasMore={hasMore}
            scrollThreshold="200px"
            loader={
              <div className="text-center mt-5" ref={loaderRef}>
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            }
            style={{ overflow: "hidden" }}
          >
            <Row className="align-items-stretch">
              {sliderData.map((item, index) => (
                <Col lg={3} md={4} sm={6} className="mb-4" key={index}>
                  <div className={"h-100 " + styles["card-blog"]}>
                    <div className={styles["blog-image"]}>
                      <Image
                        height={400}
                        width={298}
                        src={
                          validateImgUrl(item?.og_image)
                            ? item?.og_image
                            : dummyImg
                        }
                        alt={item?.title}
                      />
                    </div>
                    <div className={styles["blog-content"]}>
                      <div className={styles["blog-date"]}>
                        {moment
                          .tz(item.updated_at, "UTC")
                          .format("DD MMM YYYY")}
                      </div>
                      <h4>
                        {item?.title?.length > 60
                          ? `${item?.title?.slice(0, 60)}.....`
                          : item?.title}
                      </h4>

                      <Link
                        href={`/blogs/${item?.slug}`}
                        className={styles["theme-btn"]}
                      >
                        Read More
                      </Link>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </InfiniteScroll>
        </Container>
      </section>

      <Link
        id="floatingBook"
        className="floating-book"
        aria-label="Book a FREE call now"
        href="/healers"
        style={{ opacity: showSideButton ? 1 : 0 }}
      >
        Book a FREE call now
      </Link>
    </>
  );
};

export default Blogs;

Blogs.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

// SSR
export async function getServerSideProps() {
  const fetchSliderData = async () => {
    try {
      const response = await healerServices.getBlogsData(false);
      if (response?.status) return response.data.data;
    } catch (e) {
      console.error(e);
    }
  };

  const fetchBlogsMeta = async () => {
    try {
      const res = await healerServices.getMetaInformation("blogs");
      if (res?.status) return res.data;
    } catch (e) {
      console.error(e);
    }
  };

  const sliderData = (await fetchSliderData()) ?? {};
  const blogsMeta = (await fetchBlogsMeta()) ?? {};

  return { props: { sliderData, blogsMeta } };
}